// Metal Deck Configuration

import type { ChartImage, DropdownOption, LivePlotImageOptions, LivePlotSizeOptions, PlotSpec, PrintChartAspect, RichSummary } from '../shared';

export const APP_NAME = 'iMetalDeck';
export const APP_VERSION = '1.1.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };

export type APIPayload = {
  title: string;
  span: string;
  addedWeight: string;
  supports: number;
  sectionName: string;
  material: number;
  numTroughs: number;
  Pa: string; // pressure history csv
  td: string; // time history csv
  d0: string; // initial displacement
  v0: string; // initial velocity
};

export type ChartKey = 'chart_load' | 'chart_disp' | 'chart_rot' | 'chart_reaction' | 'chart_resistance' | 'chart_PM';

export const LIVE_CHART_ORDER: ChartKey[] = ['chart_disp', 'chart_rot', 'chart_load', 'chart_reaction', 'chart_resistance', 'chart_PM'];

export const PRINT_CHART_ORDER: ChartKey[] = ['chart_load', 'chart_disp', 'chart_rot', 'chart_reaction', 'chart_resistance', 'chart_PM'];

export const PRINT_CHART_ASPECT = {
  chart_load: 'wide',
  chart_disp: 'wide',
  chart_rot: 'wide',
  chart_reaction: 'wide',
  chart_resistance: 'square',
  chart_PM: 'square'
} satisfies Record<ChartKey, PrintChartAspect>;

export const PRINT_CHART_IMAGE_OPTIONS: Record<PrintChartAspect, LivePlotImageOptions> = {
  wide: {
    width: 600,
    height: 400,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 28 } },
      margin: { l: 86, r: 28, t: 72, b: 108 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 18 }, x: 0.5, xanchor: 'center', y: -0.32 }
    }
  },
  square: {
    width: 600,
    height: 600,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 28 } },
      margin: { l: 88, r: 28, t: 78, b: 114 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 18 }, x: 0.5, xanchor: 'center', y: -0.32 }
    }
  }
};

export const LIVE_PLOT_CARD_MIN_HEIGHT = 'clamp(18rem, 70vw, 25rem)';

export const LIVE_PLOT_SIZE_BY_KEY = {
  chart_load: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_disp: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_rot: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_reaction: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '4 / 3'
  },
  chart_resistance: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '1 / 1'
  },
  chart_PM: {
    fit: 'width',
    width: '100%',
    maxWidth: '30rem',
    aspectRatio: '1 / 1'
  }
} satisfies Record<ChartKey, LivePlotSizeOptions>;

// prettier-ignore
export const SECTION_NAMES = [
  {value:'Deep Vercor, GA 26', label:'Deep Vercor, GA 26, Depth=1.3125, Width=4.5'},
  {value:'Deep Vercor, GA 24', label:'Deep Vercor, GA 24, Depth=1.3125, Width=4.5'},
  {value:'Deep Vercor, GA 22', label:'Deep Vercor, GA 22, Depth=1.3125, Width=4.5'},
  {value:'Deep Vercor, GA 20', label:'Deep Vercor, GA 20, Depth=1.3125, Width=4.5'},
  {value:'Shallow Vercor, GA 28', label:'Shallow Vercor, GA 28, Depth=0.5625, Width=3.0'},
  {value:'Shallow Vercor, GA 26', label:'Shallow Vercor, GA 26, Depth=0.5625, Width=3.0'},
  {value:'Shallow Vercor, GA 24', label:'Shallow Vercor, GA 24, Depth=0.5625, Width=3.0'},
  {value:'Shallow Vercor, GA 22', label:'Shallow Vercor, GA 22, Depth=0.5625, Width=3.0'},
  {value:'B Formlok, GA 22', label:'B Formlok, GA 22, Depth=1.5, Width=6.0'},
  {value:'B Formlok, GA 20', label:'B Formlok, GA 20, Depth=1.5, Width=6.0'},
  {value:'B Formlok, GA 18', label:'B Formlok, GA 18, Depth=1.5, Width=6.0'},
  {value:'B Formlok, GA 16', label:'B Formlok, GA 16, Depth=1.5, Width=6.0'},
  {value:'W2 Formlok, GA 22', label:'W2 Formlok, GA 22, Depth=2.0, Width=12.0'},
  {value:'W2 Formlok, GA 21', label:'W2 Formlok, GA 21, Depth=2.0, Width=12.0'},
  {value:'W2 Formlok, GA 20', label:'W2 Formlok, GA 20, Depth=2.0, Width=12.0'},
  {value:'W2 Formlok, GA 19', label:'W2 Formlok, GA 19, Depth=2.0, Width=12.0'},
  {value:'W2 Formlok, GA 18', label:'W2 Formlok, GA 18, Depth=2.0, Width=12.0'},
  {value:'W2 Formlok, GA 16', label:'W2 Formlok, GA 16, Depth=2.0, Width=12.0'},
  {value:'W3 Formlok, GA 22', label:'W3 Formlok, GA 22, Depth=3.0, Width=12.0'},
  {value:'W3 Formlok, GA 21', label:'W3 Formlok, GA 21, Depth=3.0, Width=12.0'},
  {value:'W3 Formlok, GA 20', label:'W3 Formlok, GA 20, Depth=3.0, Width=12.0'},
  {value:'W3 Formlok, GA 19', label:'W3 Formlok, GA 19, Depth=3.0, Width=12.0'},
  {value:'W3 Formlok, GA 18', label:'W3 Formlok, GA 18, Depth=3.0, Width=12.0'},
  {value:'W3 Formlok, GA 16', label:'W3 Formlok, GA 16, Depth=3.0, Width=12.0'},
  {value:'N Formlok, GA 22', label:'N Formlok, GA 22, Depth=3.0, Width=8.0'},
  {value:'N Formlok, GA 20', label:'N Formlok, GA 20, Depth=3.0, Width=8.0'},
  {value:'N Formlok, GA 18', label:'N Formlok, GA 18, Depth=3.0, Width=8.0'},
  {value:'N Formlok, GA 16', label:'N Formlok, GA 16, Depth=3.0, Width=8.0'},
  {value:'PLB-36/HSB-36, GA 22', label:'PLB-36/HSB-36, GA 22, Depth=1.5, Width=6.0'},
  {value:'PLB-36/HSB-36, GA 20', label:'PLB-36/HSB-36, GA 20, Depth=1.5, Width=6.0'},
  {value:'PLB-36/HSB-36, GA 18', label:'PLB-36/HSB-36, GA 18, Depth=1.5, Width=6.0'},
  {value:'PLB-36/HSB-36, GA 16', label:'PLB-36/HSB-36, GA 16, Depth=1.5, Width=6.0'},
  {value:'PLN-24/N-24, GA 22', label:'PLN-24/N-24, GA 22, Depth=3.0, Width=8.0'},
  {value:'PLN-24/N-24, GA 20', label:'PLN-24/N-24, GA 20, Depth=3.0, Width=8.0'},
  {value:'PLN-24/N-24, GA 18', label:'PLN-24/N-24, GA 18, Depth=3.0, Width=8.0'},
  {value:'PLN-24/N-24, GA 16', label:'PLN-24/N-24, GA 16, Depth=3.0, Width=8.0'},
  {value:'Vulcraft Type-B, GA 24', label:'Vulcraft Type-B, GA 24, Depth=1.5, Width=6.0'},
  {value:'Vulcraft Type-B, GA 22', label:'Vulcraft Type-B, GA 22, Depth=1.5, Width=6.0'},
  {value:'Vulcraft Type-B, GA 21', label:'Vulcraft Type-B, GA 21, Depth=1.5, Width=6.0'},
  {value:'Vulcraft Type-B, GA 20', label:'Vulcraft Type-B, GA 20, Depth=1.5, Width=6.0'},
  {value:'Vulcraft Type-B, GA 19', label:'Vulcraft Type-B, GA 19, Depth=1.5, Width=6.0'},
  {value:'Vulcraft Type-B, GA 18', label:'Vulcraft Type-B, GA 18, Depth=1.5, Width=6.0'},
  {value:'Vulcraft Type-B, GA 16', label:'Vulcraft Type-B, GA 16, Depth=1.5, Width=6.0'},
  {value:'Vulcraft Type-N, GA 22', label:'Vulcraft Type-N, GA 22, Depth=3.0, Width=8.0'},
  {value:'Vulcraft Type-N, GA 21', label:'Vulcraft Type-N, GA 21, Depth=3.0, Width=8.0'},
  {value:'Vulcraft Type-N, GA 20', label:'Vulcraft Type-N, GA 20, Depth=3.0, Width=8.0'},
  {value:'Vulcraft Type-N, GA 19', label:'Vulcraft Type-N, GA 19, Depth=3.0, Width=8.0'},
  {value:'Vulcraft Type-N, GA 18', label:'Vulcraft Type-N, GA 18, Depth=3.0, Width=8.0'},
  {value:'Vulcraft Type-N, GA 16', label:'Vulcraft Type-N, GA 16, Depth=3.0, Width=8.0'},
];

export const MATERIAL_OPTIONS: DropdownOption[] = [
  { value: 33, label: '33 ksi' },
  { value: 50, label: '50 ksi' }
];

export type APIResponse = {
  summary: RichSummary;
  plotData: Partial<Record<ChartKey, PlotSpec>>;
  cross_section?: ChartImage;
};

// Plotly chart images are generated client-side for print/save.
export type MetalDeckResultState = APIResponse & {
  charts?: Partial<Record<ChartKey, ChartImage>>;
};
